import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import useReveal from "@/hooks/useReveal";
import { setPageSEO } from "@/lib/seo";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import ContactForm from "@/components/landing/ContactForm";
import { MessageCircle, Mail, Clock } from "lucide-react";

const WHATSAPP_URL = `https://wa.me/56999176009?text=${encodeURIComponent("Hola Carlos, me gustaría conversar sobre EOS para mi empresa.")}`;

export default function Contacto() {
  useReveal();

  useEffect(() => {
    setPageSEO({
      title: "Contacto | Carlos Garcín · Professional EOS Implementer",
      description: "Agenda una llamada introductoria con Carlos Garcín, Implementador EOS en Chile y LatAm. Cuéntame de tu empresa y te contacto a la brevedad.",
      path: "/contacto",
      keywords: "contacto EOS, agendar llamada EOS, Carlos Garcín contacto, implementador EOS Chile, llamada introductoria EOS",
    });
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Inicio", item: "https://garcin.cl/" },
      { "@type": "ListItem", position: 2, name: "Contacto", item: "https://garcin.cl/contacto" },
    ],
  };

  return (
    <>
      <Header />
      <main data-testid="contacto-page">
        <section className="bg-[#0A1128] pt-28 pb-10">
          <div className="container-eos">
            <p className="text-[11px] font-bold tracking-[0.28em] uppercase text-[#F26522]">
              Contacto
            </p>
            <h1 className="mt-4 font-display font-extrabold text-white leading-[1.05] tracking-[-0.02em] text-3xl sm:text-4xl lg:text-5xl max-w-3xl">
              Te cuento sobre EOS, <span className="text-[#F26522]">sin compromiso</span>
            </h1>
            <p className="mt-5 text-slate-300 text-base sm:text-lg max-w-2xl leading-relaxed">
              Cuéntame brevemente sobre tu empresa y te contacto a la brevedad.
              Una llamada puede aclarar más que cualquier página web.
            </p>
          </div>
        </section>

        <section className="py-10 lg:py-14 bg-white">
          <div className="container-eos">
            <div className="grid lg:grid-cols-[1.15fr_330px] gap-10 lg:gap-14 items-start max-w-6xl mx-auto">
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-7 sm:p-10 reveal">
                <p className="text-slate-600 text-base sm:text-lg">
                  Completa el formulario y coordinamos una llamada introductoria.
                </p>
                <div className="mt-7">
                  <ContactForm />
                </div>
              </div>

              <aside className="space-y-5 reveal" data-testid="contacto-direct-channels">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contacto-whatsapp-card"
                  className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-5 hover:border-[#F26522]/40 transition-colors"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#25D366]/10 flex items-center justify-center shrink-0">
                    <MessageCircle className="w-5 h-5 text-[#25D366]" strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="font-display font-bold text-navy-800">WhatsApp</p>
                    <p className="text-sm text-slate-600 mt-0.5">+56 9 9917 6009</p>
                  </div>
                </a>

                <a
                  href="mailto:carlos.garcin@eosworldwide.com"
                  data-testid="contacto-email-card"
                  className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-5 hover:border-[#F26522]/40 transition-colors"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#F26522]/10 flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-[#F26522]" strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="font-display font-bold text-navy-800">Email</p>
                    <p className="text-sm text-slate-600 mt-0.5 break-all">carlos.garcin@eosworldwide.com</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <div className="w-11 h-11 rounded-xl bg-navy-800/5 flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-navy-800" strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="font-display font-bold text-navy-800">Tiempo de respuesta</p>
                    <p className="text-sm text-slate-600 mt-0.5">
                      Respondo personalmente, normalmente dentro del mismo día.
                    </p>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloat />
      <Toaster position="top-right" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  );
}
